import React, { useEffect, useState, useRef } from 'react';
import InfoBanner from '../components/InfoBanner';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
    fetchClickhouseDatabases, deleteClickhouseDatabase, addClickhouseDatabase,
    updateClickhouseDatabaseStatus, ClickhouseDatabase,
} from '../store/slices/clickhouseDatabasesSlice';
import { fetchFlavors } from '../store/slices/flavorsSlice';
import axiosInstance from '../utility/axiosInstance';
import { useToast } from '../components/ToastContext';
import { useConfirm } from '../components/ConfirmContext';
import Select, { SingleValue } from 'react-select';
import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import Modal from '../components/Modal';
import ManagedBackupsModal from '../components/ManagedBackupsModal';
import HaOption, { HA_MEMBERS, haMemberCount } from '../components/HaOption';
import UpgradeToHaModal, { haUpgradeOption } from '../components/UpgradeToHaModal';
import TrialLock from '../components/TrialLock';
import { useLimitedTrial } from '../hooks/useLimitedTrial';
import useAutoOpenCreateModal from '../hooks/useAutoOpenCreateModal';
import { FaPlus, FaSyncAlt, FaRegCopy, FaCheck } from 'react-icons/fa';
import '../styles/InstancesPage.scss';
import '../styles/DatabasesPage.scss';
import { isFlavorOptionDisabled, platformImage, toFlavorOptions } from '../utility/flavorOptions';
import { NameField, SecretField, formatSizeOption } from '../components/CreateFormFields';
import { secretOk } from '../utility/fieldRules';

const ITEMS_PER_PAGE = 10;

type SelectOption = { value: string; label: string; isDisabled?: boolean };

// ClickHouse is an OLAP / columnar analytics engine (Apache-2.0) - its own managed resource, NOT a
// classical relational DB. Reached over its HTTP interface (port 8123) from the same private network.
const ClickHousePage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const [backupsFor, setBackupsFor] = useState<any>(null);
    const [upgradeFor, setUpgradeFor] = useState<ClickhouseDatabase | null>(null);
    const isTrial = useLimitedTrial();
    const { databases, status } = useSelector((s: RootState) => s.clickhouseDatabases);
    const flavors = useSelector((s: RootState) => s.flavors.data);
    const images = useSelector((s: RootState) => s.images.data);
    const vmInstances = useSelector((s: RootState) => s.instances.data);

    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newDb, setNewDb] = useState<{ name: string; db_name: string; db_user: string; db_password: string }>(
        { name: '', db_name: 'analytics', db_user: 'appuser', db_password: '' }
    );
    const [flavorOption, setFlavorOption] = useState<SelectOption | null>(null);
    const [keypairOption, setKeypairOption] = useState<SelectOption | null>(null);
    const [keypairs, setKeypairs] = useState<SelectOption[]>([]);
    // High availability: 3 machines behind a load balancer instead of 1, so ~3x the hourly bill.
    const [ha, setHa] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [networkId, setNetworkId] = useState<string | null>(null);
    const [justCreated, setJustCreated] = useState<ClickhouseDatabase & { db_password: string } | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const pollRefs = useRef<Record<number, NodeJS.Timeout>>({});

    const creationLocked = useAutoOpenCreateModal(setShowCreate);

    useEffect(() => {
        dispatch(fetchClickhouseDatabases());
        dispatch(fetchFlavors());
    }, [dispatch]);

    useEffect(() => {
        if (!showCreate) return;
        Promise.all([
            axiosInstance.get('/v1/keypairs'),
            axiosInstance.get('/v1/private_networks'),
        ]).then(([kpRes, netRes]) => {
            const kps = (Array.isArray(kpRes.data) ? kpRes.data : [])
                .map((k: any) => ({ value: k.keypair?.name ?? '', label: k.keypair?.name ?? '' }))
                .filter((k: SelectOption) => k.value);
            setKeypairs(kps);
            if (kps.length === 1) setKeypairOption(kps[0]);
            const nets = Array.isArray(netRes.data) ? netRes.data : [];
            if (nets[0]?.id) setNetworkId(nets[0].id);
        }).catch((_e) => { /* ignore */ });
    }, [showCreate]);

    useEffect(() => {
        // Single-machine databases only. This poll flips the row to "ready" as soon as ONE VM named
        // rdb-<name> is ACTIVE, which is a fine signal for one machine and a wrong one for three: an
        // HA database with one member up is not ready, and PATCHing it ready here would write that
        // wrong state back to the API. HA readiness is decided by the workflow that builds it, so
        // the row updates on the next fetch instead.
        databases.filter(d => d.status === 'provisioning' && !d.ha).forEach(db => {
            if (pollRefs.current[db.id]) return;
            pollRefs.current[db.id] = setInterval(async () => {
                try {
                    const listRes = await axiosInstance.get('/v1/instances');
                    const instances = Array.isArray(listRes.data) ? listRes.data : [];
                    const inst = instances.find((i: any) => i.name === `rdb-${db.name}` && i.status === 'ACTIVE');
                    if (inst) {
                        const ip = inst.metadata?.floatingIp ?? null;
                        clearInterval(pollRefs.current[db.id]);
                        delete pollRefs.current[db.id];
                        await axiosInstance.patch(`/v1/clickhouse/${db.id}`, { status: 'ready', floating_ip: ip });
                        dispatch(updateClickhouseDatabaseStatus({ id: db.id, status: 'ready', floating_ip: ip }));
                    }
                } catch (_e) { /* keep polling */ }
            }, 15000);
        });
        return () => { Object.values(pollRefs.current).forEach(clearInterval); };
    }, [databases, dispatch]);

    useEffect(() => { setCurrentPage(0); }, [databases.length]);

    const totalDatabases = databases.length;
    const totalPages = Math.ceil(totalDatabases / ITEMS_PER_PAGE);
    const paginatedDatabases = databases.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

    // Every number from /v1/flavors; a size the datastore image cannot boot is locked with the reason, from that image's
    // own min_ram/min_disk (/v1/images), as on the instance form. One machine's price.
    const flavorOptions: SelectOption[] = toFlavorOptions(flavors, undefined, { image: platformImage(images, 'datastore') });

    // The rate the HA cost estimate multiplies by. Same source the instance page uses: the selected
    // flavor's real hourly_rate from /v1/flavors (pricing_rules), never a hardcoded price table.
    const selectedFlavorRate: number | null = (() => {
        if (!flavorOption) return null;
        const f: any = (Array.isArray(flavors) ? flavors : []).find((x: any) => String(x.id) === flavorOption.value);
        const rate = f?.hourly_rate;
        return rate != null && Number.isFinite(Number(rate)) ? Number(rate) : null;
    })();

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!flavorOption) { showToast('Select a server size', 'error'); return; }
        if (!keypairOption) { showToast('Select an SSH keypair', 'error'); return; }
        if (!networkId) { showToast('No private network found. Create one first.', 'error'); return; }
        if (!newDb.name.trim()) { showToast('Enter a name', 'error'); return; }

        setCreating(true);
        try {
            const res = await axiosInstance.post('/v1/clickhouse', {
                name: newDb.name.trim(),
                db_name: newDb.db_name.trim() || 'analytics',
                db_user: newDb.db_user.trim() || 'appuser',
                db_password: newDb.db_password.trim() || undefined,
                flavor_id: flavorOption.value,
                network_id: networkId,
                keypair_name: keypairOption.value,
                security_group_names: ['default'],
                imageName: 'ubuntu',
                // Only sent when asked for. A create that omits the field keeps working against an
                // API build that does not know about HA yet; sending ha:false everywhere would not.
                ...(ha ? { ha: true } : {}),
            });
            setJustCreated(res.data);
            dispatch(addClickhouseDatabase(res.data));
            setShowCreate(false);
            setNewDb({ name: '', db_name: 'analytics', db_user: 'appuser', db_password: '' });
            setFlavorOption(null);
            setHa(false);
            showToast(ha
                ? `Highly available ClickHouse provisioning started - ${HA_MEMBERS} machines, billed as ${HA_MEMBERS}`
                : 'ClickHouse provisioning started', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.error ?? 'Failed to create ClickHouse database', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (db: ClickhouseDatabase) => {
        const proceed = await confirm({
            title: 'Delete ClickHouse',
            message: `Delete "${db.name}"? This will destroy the server and all data.`,
            confirmText: 'Delete', cancelText: 'Cancel', type: 'danger',
        });
        if (!proceed) return;
        setDeletingId(db.id);
        try {
            await dispatch(deleteClickhouseDatabase(db.id)).unwrap();
            showToast(`${db.name} deleted`, 'success');
        } catch (err: any) {
            showToast(err.message ?? 'Failed to delete', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const handleCopyConnection = (db: ClickhouseDatabase) => {
        const str = connectionString(db);
        if (!str) return;
        const doCopy = () => {
            if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(str);
            const el = document.createElement('textarea');
            el.value = str; el.style.position = 'fixed'; el.style.opacity = '0';
            document.body.appendChild(el); el.focus(); el.select();
            document.execCommand('copy'); document.body.removeChild(el);
            return Promise.resolve();
        };
        doCopy().then(() => {
            setCopiedId(db.id);
            showToast('Copied - fill in the password shown when the database was created (it is not stored)', 'success');
            setTimeout(() => setCopiedId(null), 2000);
        }).catch(() => showToast('Failed to copy', 'error'));
    };

    // Reach it from an instance on the same private network via the DB's internal IP. ClickHouse VMs
    // share the relational `rdb-<name>` naming, so fall back to the VM's fixed/floating IP.
    const hostFor = (db: ClickhouseDatabase): string | null => {
        // An HA database is reached through the load balancer address, never through a member's own
        // IP: the member behind that IP is replaced whenever it is rebuilt, and a connection string
        // pinned to it would break silently at exactly the moment failover was supposed to save it.
        if (db.ha && db.vip) return db.vip;
        if (db.private_ip) return db.private_ip;
        const vm: any = (vmInstances ?? []).find((i: any) => i.id === (db as any).instance_id);
        const internalIp = vm
            ? (Object.values(vm.addresses ?? {}).flat() as any[]).find(a => a?.["OS-EXT-IPS:type"] === "fixed")?.addr
            : null;
        return internalIp ?? db.floating_ip ?? null;
    };

    // ClickHouse HTTP interface (port 8123). The DB is passed as a ?database= query param.
    const connectionString = (db: ClickhouseDatabase) => {
        const host = hostFor(db);
        return host ? `http://${db.db_user}:****@${host}:${db.port}/?database=${db.db_name}` : null;
    };
    const fullConnectionString = (db: ClickhouseDatabase & { db_password?: string }) => {
        const host = hostFor(db);
        return host ? `http://${db.db_user}:${db.db_password ?? '****'}@${host}:${db.port}/?database=${db.db_name}` : null;
    };

    // Availability, reported only as far as the API actually described it. `ha` missing entirely
    // (an older row, or a build whose SELECT does not list the column) renders nothing: claiming
    // "single machine" for a database we were told nothing about would be a guess presented as a
    // fact, and the member count is shown only when member_ips came back with members in it.
    const haBadge = (db: ClickhouseDatabase) => {
        // An upgrade in flight is its own state and its own colour. The row still reads
        // status='ready' throughout, correctly - the SINGLE machine is still serving.
        if (!db.ha && db.ha_upgrade_in_progress) {
            return <span className="db-ha-upgrading-badge">Upgrading to HA…</span>;
        }
        if (!db.ha) return null;
        const members = haMemberCount(db.member_ips);
        return <span className="db-ha-badge">{members ? `HA · ${members} machines` : 'HA'}</span>;
    };
    const availabilityLabel = (db: ClickhouseDatabase): string | null => {
        if (db.ha === undefined) return null;
        if (!db.ha) return 'Single machine';
        const members = haMemberCount(db.member_ips);
        return members ? `High availability, ${members} machines` : 'High availability';
    };

    // ONE Actions menu for the table and the mobile cards. Two hand-maintained copies is how a
    // new action reaches one of them and not the other.
    const actionOptions = (db: ClickhouseDatabase) => {
        const upgrade = haUpgradeOption(db, isTrial);
        return [
            ...(upgrade ? [upgrade] : []),
            { value: 'backups', label: 'Backups' },
            { value: 'delete', label: 'Delete ClickHouse' },
        ];
    };
    const handleAction = (db: ClickhouseDatabase, action?: string) => {
        if (action === 'delete') handleDelete(db);
        else if (action === 'backups') setBackupsFor(db);
        else if (action === 'upgrade-ha') setUpgradeFor(db);
    };

    // The real hourly rate of this database's current size, so the upgrade modal prices the change
    // rather than saying "about 3x". Null when the size is unknown, and the modal says so.
    const hourlyRateFor = (db: ClickhouseDatabase): number | null => {
        const fl: any = (Array.isArray(flavors) ? flavors : []).find((f: any) => String(f.id) === String((db as any).flavor_id));
        const rate = fl?.hourly_rate;
        return rate != null && Number.isFinite(Number(rate)) ? Number(rate) : null;
    };

    const isLoading = status === 'loading';

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <p className="page-subtitle">
                    Managed ClickHouse - a dedicated column-store for analytics &amp; observability. Ready in minutes.
                </p>
                <a href="https://docs.redu.cloud/Guides/databases" target="_blank" rel="noopener noreferrer" className="docs-link">
                    Documentation →
                </a>
            </div>

            <InfoBanner id="clickhouse-private-access">
                <span>ClickHouse is <strong>private</strong> - reachable only from an instance on the <strong>same private network</strong>, over its HTTP interface using the database&apos;s private IP (e.g. <code>http://appuser:****@10.1.x.x:8123/?database=analytics</code>). It is not exposed to the public internet.</span>
            </InfoBanner>

            <TrialLock>
                <button className="create-button" onClick={() => setShowCreate(true)} title="New ClickHouse">
                    <FaPlus />
                    <span>New ClickHouse</span>
                </button>
            </TrialLock>

            {justCreated && (
                <div className="db-credentials-banner">
                    <div className="db-cred-header">
                        <span className="db-cred-title">✓ <strong>{justCreated.name}</strong> is provisioning</span>
                        <button className="db-cred-dismiss" onClick={() => setJustCreated(null)}>✕</button>
                    </div>
                    <p className="db-cred-note">Save these credentials now - the password won&apos;t be shown again.</p>
                    <div className="db-cred-grid">
                        <div className="db-cred-row"><span>Engine</span><code>ClickHouse</code></div>
                        {availabilityLabel(justCreated) && (
                            <div className="db-cred-row"><span>Availability</span><code>{availabilityLabel(justCreated)}</code></div>
                        )}
                        <div className="db-cred-row"><span>Host</span><code>{hostFor(justCreated) ?? 'Provisioning… check back in 5 min'}</code></div>
                        <div className="db-cred-row"><span>Port (HTTP)</span><code>8123</code></div>
                        <div className="db-cred-row"><span>Database</span><code>{justCreated.db_name}</code></div>
                        <div className="db-cred-row"><span>User</span><code>{justCreated.db_user}</code></div>
                        <div className="db-cred-row"><span>Password</span><code>{justCreated.db_password}</code></div>
                        {fullConnectionString(justCreated) && (
                            <div className="db-cred-row">
                                <span>Connection (HTTP)</span>
                                <code className="db-cred-conn" onClick={() => { navigator.clipboard?.writeText(fullConnectionString(justCreated)!); showToast('Copied', 'success'); }}>
                                    {fullConnectionString(justCreated)} <span className="copy-hint">click to copy</span>
                                </code>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Database</th>
                                <th>Connection (HTTP :8123)</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan={5} className="loading-state"><FaSyncAlt className="spin" /> Loading databases...</td></tr>
                            ) : paginatedDatabases.length > 0 ? (
                                paginatedDatabases.map(db => {
                                    const conn = connectionString(db);
                                    return (
                                    <tr key={db.id}>
                                        <td className="keypair-name">{db.name}{haBadge(db)}</td>
                                        <td><code>{db.db_name}</code></td>
                                        <td className="db-conn">
                                            {conn ? (
                                                <div className="db-conn-wrapper">
                                                    <code>{conn}</code>
                                                    <button className={`db-copy-btn${copiedId === db.id ? ' copied' : ''}`} onClick={() => handleCopyConnection(db)} title="Copy connection string">
                                                        {copiedId === db.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </div>
                                            ) : (<span className="db-provisioning">Provisioning…</span>)}
                                        </td>
                                        <td><span className={`db-status db-status-${deletingId === db.id ? 'provisioning' : db.status}`}>{deletingId === db.id ? 'deleting…' : db.status}</span></td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <Select
                                                options={actionOptions(db)}
                                                onChange={opt => handleAction(db, opt?.value)}
                                                placeholder="Actions" isSearchable={false}
                                                menuPortalTarget={document.body} menuPosition="fixed" styles={getActionSelectStyles()}
                                            />
                                        </td>
                                    </tr>
                                    );
                                })
                            ) : (
                                <tr><td colSpan={5} className="empty-state">No ClickHouse databases yet</td></tr>
                            )}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                            <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}>← Prev</button>
                            <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalDatabases)} of {totalDatabases}
                            </span>
                            <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}>Next →</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="mobile-view">
                {isLoading ? (
                    <div className="loading-state"><FaSyncAlt className="spin" /><p>Loading databases...</p></div>
                ) : (
                    <div className="cards-container">
                        {paginatedDatabases.length > 0 ? paginatedDatabases.map(db => {
                            const conn = connectionString(db);
                            return (
                            <div key={db.id} className="keypair-card">
                                <div className="card-header">
                                    <h3 className="card-title">{db.name}</h3>
                                    <div style={{ width: '100%', marginTop: 12 }}>
                                        <Select options={actionOptions(db)}
                                            onChange={opt => handleAction(db, opt?.value)}
                                            placeholder="Actions" isSearchable={false} menuPortalTarget={document.body} styles={getActionSelectStyles()} />
                                    </div>
                                </div>
                                <div className="card-content">
                                    <div className="fingerprint"><span className="label">Engine:</span><span className="value">ClickHouse</span></div>
                                    <div className="fingerprint"><span className="label">Database:</span><span className="value">{db.db_name}</span></div>
                                    {availabilityLabel(db) && (
                                        <div className="fingerprint"><span className="label">Availability:</span><span className="value">{availabilityLabel(db)}</span></div>
                                    )}
                                    <div className="fingerprint"><span className="label">Status:</span><span className="value"><span className={`db-status db-status-${deletingId === db.id ? 'provisioning' : db.status}`}>{deletingId === db.id ? 'deleting…' : db.status}</span></span></div>
                                    <div className="fingerprint">
                                        <span className="label">Connection:</span>
                                        <span className="value db-conn">
                                            {conn ? (
                                                <span className="db-conn-wrapper">
                                                    <span>{conn}</span>
                                                    <button className={`db-copy-btn${copiedId === db.id ? ' copied' : ''}`} onClick={() => handleCopyConnection(db)} title="Copy connection string">
                                                        {copiedId === db.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </span>
                                            ) : 'Provisioning…'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            );
                        }) : (
                            <div className="empty-state"><p>No ClickHouse databases yet</p></div>
                        )}
                    </div>
                )}
            </div>

            <ManagedBackupsModal
                isOpen={!!backupsFor}
                onClose={() => setBackupsFor(null)}
                apiBase="/v1/clickhouse"
                serviceId={backupsFor?.id ?? null}
                serviceName={backupsFor?.name ?? ''}
            />

            {/* Upgrade a live single machine to high availability, in place */}
            <UpgradeToHaModal
                isOpen={!!upgradeFor}
                onClose={() => setUpgradeFor(null)}
                target={upgradeFor ? {
                    id: upgradeFor.id,
                    name: upgradeFor.name,
                    serviceLabel: 'ClickHouse',
                    apiBase: '/v1/clickhouse',
                    hourlyRate: hourlyRateFor(upgradeFor),
                } : null}
                onStarted={() => { dispatch(fetchClickhouseDatabases()); }}
            />

            <Modal isOpen={showCreate && !creationLocked} onClose={() => setShowCreate(false)} title="New ClickHouse Database">
                <form onSubmit={handleCreate} className="create-form">
                    <NameField value={newDb.name} onChange={v => setNewDb(p => ({ ...p, name: v }))} placeholder="my-analytics" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div className="form-group">
                            <label>Database name</label>
                            <input value={newDb.db_name} onChange={e => setNewDb(p => ({ ...p, db_name: e.target.value }))} placeholder="analytics" />
                        </div>
                        <div className="form-group">
                            <label>Username</label>
                            <input value={newDb.db_user} onChange={e => setNewDb(p => ({ ...p, db_user: e.target.value }))} placeholder="appuser" />
                        </div>
                    </div>
                    <SecretField label="Password" value={newDb.db_password} onChange={v => setNewDb(p => ({ ...p, db_password: v }))} charset />
                    <div className="form-group">
                        <label>Server size <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select options={flavorOptions} value={flavorOption}
                        isOptionDisabled={isFlavorOptionDisabled as any}
                            onChange={(v: SingleValue<SelectOption>) => setFlavorOption(v)}
                            formatOptionLabel={formatSizeOption}
                            placeholder="Select server size…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    <div className="form-group">
                        <label>SSH keypair <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select options={keypairs} value={keypairOption}
                            onChange={(v: SingleValue<SelectOption>) => setKeypairOption(v)}
                            placeholder="Select keypair…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    {/* Directly above the summary and the submit button, so the 3x cost is the last
                        thing read before Create is pressed. */}
                    <HaOption
                        enabled={ha}
                        onChange={setHa}
                        hourlyRate={selectedFlavorRate}
                        serviceLabel="ClickHouse"
                    />
                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowCreate(false)}>Cancel</button>
                        <button type="submit" className="submit-button" disabled={creating || !flavorOption || !keypairOption || !newDb.name || !secretOk(newDb.db_password, { charset: true })}>
                            {creating ? 'Creating…' : ha ? `Create ClickHouse (${HA_MEMBERS} machines)` : 'Create ClickHouse'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ClickHousePage;
